import { z } from "zod";

export const VerifyPasswordRequestSchema = z.object({
  password: z.string().min(1, "비밀번호를 입력해주세요"),
});

export type VerifyPasswordRequest = z.infer<typeof VerifyPasswordRequestSchema>;

export const SocialLoginRequestSchema = z.object({
  providerUserToken: z.string(),
  providerUserId: z.string(),
  companyId: z.number().optional(),
});

export type SocialLoginRequest = z.infer<typeof SocialLoginRequestSchema>;

export const LoginResponseSchema = z.object({
  userId: z.number(),
  companyId: z.number().optional().nullable(),
  accessToken: z.string(),
  refreshToken: z.string(),
});

export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const SignupRequestSchema = z.object({
  email: z.string().email("유효한 이메일을 입력해주세요"),
  password: z
    .string()
    .min(8, "비밀번호는 8자 이상이어야 합니다")
    .max(16, "비밀번호는 16자 이하여야 합니다")
    .regex(/[a-zA-Z]/, "영문자를 포함해야 합니다")
    .regex(/[0-9]/, "숫자를 포함해야 합니다")
    .regex(
      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/,
      "특수문자를 포함해야 합니다"
    )
    .regex(
      /^[a-zA-Z0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]+$/,
      "영문, 숫자, 특수문자만 입력 가능합니다"
    ),
  name: z.string().min(1, "이름을 입력해주세요"),
  companyId: z.number().optional(),
  termsOfServiceAgreed: z.boolean().refine((val) => val === true, {
    message: "서비스 이용약관에 동의해주세요",
  }),
  privacyPolicyAgreed: z.boolean().refine((val) => val === true, {
    message: "개인정보 수집 및 이용에 동의해주세요",
  }),
  over14YearsOld: z.boolean().refine((val) => val === true, {
    message: "만 14세 이상 확인에 동의해주세요",
  }),
  promotionNewsSubscribed: z.boolean().optional().default(false),
});

export type SignupRequest = z.infer<typeof SignupRequestSchema>;

export const RefreshTokenResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
});

export type RefreshTokenResponse = z.infer<typeof RefreshTokenResponseSchema>;

export const LoginRequestSchema = z.object({
  email: z.string().email("유효한 이메일을 입력해주세요"),
  password: z.string().min(1, "비밀번호를 입력해주세요"),
  companyId: z.number().optional().nullable(),
});

export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const TokenResponseSchema = z.object({
  userId: z.number(),
  companyId: z.number().optional().nullable(),
  accessToken: z.string(),
  refreshToken: z.string(),
});

export type TokenResponse = z.infer<typeof TokenResponseSchema>;

// 워크스페이스 선택 (한 계정이 여러 회사에 속할 때)
//
// MES 로그인(companyId 없이 POST /api/auth/login, /api/auth/social)에서 계정이 여러 회사에 있으면
// 토큰 대신 선택 목록이 온다. 고른 뒤 POST /api/auth/workspace 로 토큰을 받는다.

export type WorkspaceRole = "COMPANY_ADMIN" | "COMPANY_MANAGER" | "USER" | "ADMIN";

export interface WorkspaceOption {
  userId: number;
  companyId: number;
  companyName: string;
  role: WorkspaceRole;
}

export interface WorkspaceSelectionResponse {
  requiresWorkspaceSelection: true;
  /** 5분 유효 */
  selectionToken: string;
  workspaces: WorkspaceOption[];
}

export const isWorkspaceSelection = (data: unknown): data is WorkspaceSelectionResponse =>
  typeof data === "object" &&
  data !== null &&
  (data as { requiresWorkspaceSelection?: unknown }).requiresWorkspaceSelection === true;

/**
 * 로그인 응답이 워크스페이스 선택을 요구할 때 저장소가 던진다.
 * usecase 가 AppError 로 감싸도 originalError 로 따라가 꺼낼 수 있다(findWorkspaceSelection).
 */
export class WorkspaceSelectionRequiredError extends Error {
  constructor(readonly selection: WorkspaceSelectionResponse) {
    super("워크스페이스를 선택해 주세요.");
    this.name = "WorkspaceSelectionRequiredError";
  }
}

/** 에러(또는 AppError.originalError 체인) 안에 워크스페이스 선택 응답이 있으면 꺼낸다 */
export const findWorkspaceSelection = (error: unknown): WorkspaceSelectionResponse | null => {
  let current: unknown = error;
  for (let depth = 0; depth < 5 && current; depth++) {
    if (current instanceof WorkspaceSelectionRequiredError) return current.selection;
    current = (current as { originalError?: unknown }).originalError;
  }
  return null;
};
